<template>
  <div class="col-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
 <div class="card-body px-0 pb-2 pe-xl-3">
    <div class="row g-2 align-items-center  px-3 px-xl-4 pt-3">

        <div class="col-auto">
            <input
                type="month"
                class="form-control form-control-sm"
                v-model="monthFilter"
                @change="fetchCalculations"
            />
        </div>

        <div class="col-auto">
            <select
                class="form-select form-select-sm"
                v-model="typeFilter"
                @change="fetchCalculations"
            >
                <option value="">{{ $t('salary_management.all_types') }}</option>
                <option value="teacher">{{ $t('salary_management.teachers') }}</option>
                <option value="employee">{{ $t('salary_management.employees') }}</option>
                <option value="user">{{ $t('salary_management.users') }}</option>
            </select>
        </div>

        <div class="col-auto ms-auto d-flex gap-2">
            <button class="btn btn-outline-secondary btn-sm" @click="onPrint" :disabled="isLoading">
                <i class="fas fa-print me-1"></i>{{ $t('salary_management.print') }}
            </button>
            <button class="btn btn-outline-success btn-sm" @click="onExportExcel" :disabled="isLoading">
                <i class="fas fa-file-excel me-1"></i>{{ $t('salary_management.export_excel') }}
            </button>
            <button class="btn btn-outline-danger btn-sm" @click="onExportPdf" :disabled="isLoading || exportingPdf">
                <i class="fas fa-file-pdf me-1"></i>{{ $t('salary_management.export_pdf') }}
            </button>
        </div>

        <div class="col-auto">
            <button
                class="btn btn-secondary btn-sm"
                @click="openProcessDialog"
                v-can="'salary_management.create'"
            >
                <i class="fas fa-calendar-plus me-2"></i>
                {{ $t('salary_management.process_monthly') }}
            </button>
        </div>

    </div>

    <div class="card mb-3 mb-xxl-5" style="margin-top: 2.5rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t('salary_management.calculations_title') }}
          </h6>
        </div>
      </div>


        <LoadingSpinner v-if="isLoading" :message="$t('common.loading')" class="py-5" />

        <template v-else>
          <!-- Desktop table -->
          <div class="table-responsive p-1 p-xl-4 d-none d-xl-block">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.employee') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.type') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.salary_type') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.status') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.basic_salary') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.lessons_amount') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.rewards') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.deductions') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.net_salary') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="calculations.length === 0">
                  <td colspan="10" class="text-center py-4 text-muted">
                    <i class="fas fa-file-invoice-dollar fa-2x mb-2"></i>
                    <p>{{ $t('salary_management.no_calculations') }}</p>
                  </td>
                </tr>
                <tr v-for="calc in calculations" :key="calc.id ?? `${calc.model_type}-${calc.model_id}`">
                  <td>
                    <div class="d-flex flex-column">
                      <span class="text-sm">{{ calc.model_name || '—' }}</span>
                      <span class="text-xs text-muted" v-if="calc.job_title">{{ calc.job_title }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-sm" :class="modelKindBadgeClass(calc.model_kind)">
                      {{ modelKindLabel(calc.model_kind) }}
                    </span>
                  </td>
                  <td><span class="text-xs">{{ salaryTypeLabel(calc.salary_type) }}</span></td>
                  <td class="text-center">
                    <span class="badge badge-sm" :class="calc.status === 'not_processed' ? 'bg-secondary' : 'bg-success'">
                      {{ calc.status === 'not_processed' ? $t('salary_management.status_not_processed') : $t('salary_management.status_processed') }}
                    </span>
                  </td>
                  <td class="text-center"><span class="text-sm">{{ basicSalaryDisplay(calc) }}</span></td>
                  <td class="text-center"><span class="text-sm">{{ calc.lessons_amount }}</span></td>
                  <td class="text-center"><span class="text-sm text-success">{{ calc.rewards }}</span></td>
                  <td class="text-center"><span class="text-sm text-danger">{{ calc.deductions }}</span></td>
                  <td class="text-center"><span class="text-sm fw-bold">{{ calc.net_salary }}</span></td>
                  <td class="text-center pe-4">
                    <router-link
                      v-if="calc.id"
                      :to="{ name: 'salary.calculationDetail', params: { id: calc.id } }"
                      class="btn btn-link text-primary p-1 mx-1"
                    >
                      <i class="fas fa-eye fa-sm"></i>
                    </router-link>
                    <span v-else class="text-muted text-xs" :title="$t('salary_management.not_processed_hint')">
                      <i class="fas fa-hourglass-half fa-sm"></i>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile cards -->
          <div class="d-block d-xl-none">
            <div v-if="calculations.length === 0" class="text-center py-4 text-muted">
              <i class="fas fa-file-invoice-dollar fa-2x mb-2"></i>
              <p>{{ $t('salary_management.no_calculations') }}</p>
            </div>
            <div
              v-for="calc in calculations"
              :key="'mobile-' + (calc.id ?? `${calc.model_type}-${calc.model_id}`)"
              class="card mb-3 mx-2"
            >
              <div class="card-body py-2">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 class="mb-0 text-sm">{{ calc.model_name || '—' }}</h6>
                    <p class="text-xs text-muted mb-1" v-if="calc.job_title">{{ calc.job_title }}</p>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <router-link
                      v-if="calc.id"
                      :to="{ name: 'salary.calculationDetail', params: { id: calc.id } }"
                      class="btn btn-link text-primary p-0"
                    >
                      <i class="fas fa-eye fa-sm"></i>
                    </router-link>
                    <span v-else class="text-muted text-xs" :title="$t('salary_management.not_processed_hint')">
                      <i class="fas fa-hourglass-half fa-sm"></i>
                    </span>
                  </div>
                </div>
                <div class="d-flex flex-wrap gap-2 my-2">
                  <span class="badge badge-sm" :class="modelKindBadgeClass(calc.model_kind)">
                    {{ modelKindLabel(calc.model_kind) }}
                  </span>
                  <span class="badge badge-sm" :class="calc.status === 'not_processed' ? 'bg-secondary' : 'bg-success'">
                    {{ calc.status === 'not_processed' ? $t('salary_management.status_not_processed') : $t('salary_management.status_processed') }}
                  </span>
                </div>
                <p class="text-xs text-muted mb-1">{{ $t('salary_management.salary_type') }}: {{ salaryTypeLabel(calc.salary_type) }}</p>
                <div class="d-flex flex-wrap gap-3 mt-1">
                  <span class="text-xs"><strong>{{ $t('salary_management.basic_salary') }}:</strong> {{ basicSalaryDisplay(calc) }}</span>
                  <span class="text-xs"><strong>{{ $t('salary_management.lessons_amount') }}:</strong> {{ calc.lessons_amount }}</span>
                  <span class="text-xs text-success"><strong>{{ $t('salary_management.rewards') }}:</strong> {{ calc.rewards }}</span>
                  <span class="text-xs text-danger"><strong>{{ $t('salary_management.deductions') }}:</strong> {{ calc.deductions }}</span>
                </div>
                <p class="text-sm fw-bold mt-2 mb-0">{{ $t('salary_management.net_salary') }}: {{ calc.net_salary }}</p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <ProcessMonthlyDialog ref="processMonthlyDialog" @process="handleProcess" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import ProcessMonthlyDialog from '@/components/features/salary/ProcessMonthlyDialog.vue';
import { useTableExport, formatPrintDate } from '@/composables/useTableExport';

const { t } = useI18n();
const store = useStore();
const toast = useToast();
const { printTable, exportExcel, exportPdf } = useTableExport();
const exportingPdf = ref(false);

const monthFilter = ref(new Date().toISOString().slice(0, 7));
const typeFilter = ref('');
const processMonthlyDialog = ref(null);

const isLoading = computed(() => !!store.getters['salaryManagement/isLoading']);
const calculations = computed(() => store.getters['salaryManagement/calculations']);

const modelKindLabel = (kind) => {
  const map = {
    teacher: t('salary_management.teachers'),
    employee: t('salary_management.employees'),
    user: t('salary_management.users'),
  };
  return map[kind] || kind;
};

const modelKindBadgeClass = (kind) => {
  const map = {
    teacher: 'bg-info',
    employee: 'bg-secondary',
    user: 'bg-dark',
  };
  return map[kind] || 'bg-secondary';
};

const salaryTypeLabel = (type) => {
  const map = {
    fixed: t('teachers.salaryTypeFixed'),
    per_lesson: t('teachers.salaryTypePerLesson'),
    fixed_plus_lessons: t('teachers.salaryTypeFixedPlusLessons'),
  };
  return type ? (map[type] || type) : '—';
};

// A "per_lesson" teacher has no fixed component at all — he's paid strictly
// per lesson taught, so the basic_salary column is meaningless for him and
// should read as a dash rather than a misleading 0.
const basicSalaryDisplay = (calc) => (calc.salary_type === 'per_lesson' ? '—' : calc.basic_salary);

const fetchCalculations = async () => {
  if (!monthFilter.value) return;
  const [year, month] = monthFilter.value.split('-');
  try {
    await store.dispatch('salaryManagement/fetchCalculations', {
      year, month: Number(month), type: typeFilter.value || undefined,
    });
  } catch (e) {
    toast.error(t('common.error'));
  }
};

const calculationColumns = () => [
  { key: 'model_name', label: t('salary_management.employee') },
  { key: 'model_kind', label: t('salary_management.type'), format: (r) => modelKindLabel(r.model_kind) },
  { key: 'salary_type', label: t('salary_management.salary_type'), format: (r) => salaryTypeLabel(r.salary_type) },
  { key: 'basic_salary', label: t('salary_management.basic_salary'), format: (r) => basicSalaryDisplay(r) },
  { key: 'lessons_amount', label: t('salary_management.lessons_amount') },
  { key: 'rewards', label: t('salary_management.rewards') },
  { key: 'deductions', label: t('salary_management.deductions') },
  { key: 'net_salary', label: t('salary_management.net_salary') },
];

const onPrint = () => {
  printTable({
    title: t('salary_management.calculations_title'),
    subtitleLines: [
      `${t('salary_management.month')}: ${monthFilter.value}`,
      `${t('salary_management.print_date')}: ${formatPrintDate()}`,
    ],
    columns: calculationColumns(),
    rows: calculations.value,
  });
};

const onExportExcel = () => {
  exportExcel({
    title: t('salary_management.calculations_title'),
    filename: `salary-calculations-${monthFilter.value}`,
    columns: calculationColumns(),
    rows: calculations.value,
  });
};

const onExportPdf = async () => {
  exportingPdf.value = true;
  try {
    await exportPdf({
      title: t('salary_management.calculations_title'),
      filename: `salary-calculations-${monthFilter.value}`,
      subtitleLines: [
        `${t('salary_management.month')}: ${monthFilter.value}`,
        `${t('salary_management.print_date')}: ${formatPrintDate()}`,
      ],
      columns: calculationColumns(),
      rows: calculations.value,
    });
  } catch (e) {
    toast.error(t('common.error'));
  } finally {
    exportingPdf.value = false;
  }
};

const openProcessDialog = () => processMonthlyDialog.value?.open();

const handleProcess = async (data) => {
  try {
    const res = await store.dispatch('salaryManagement/processMonthlySalaries', data);
    const processedCount =
      (res?.teachers_count || 0) + (res?.employees_count || 0) + (res?.users_count || 0);

    if (processedCount > 0) {
      toast.success(res?.message || t('salary_management.monthly_processed'));
    } else {
      toast.info(res?.message || t('salary_management.already_processed_month'));
    }
    monthFilter.value = data.month;
    await fetchCalculations();
  } catch (error) {
    toast.error(error?.response?.data?.message || t('salary_management.process_error'));
  }
};

onMounted(fetchCalculations);
</script>
